import { forwardRef, useEffect, useRef, useState } from 'react';
import { Input } from '@mui/joy';
import { normalizeNumericText, numericValidity, sameNumericValue } from './numericInputUtils';

// Сохраняет промежуточное «12.» даже если родитель сразу делает parseFloat.
// Неправильная вставка отклоняется целиком, а не превращается в другую сумму.
const NumericInput = forwardRef(function NumericInput({ value, defaultValue, onChange, onFocus, onBlur,
    onKeyDown, slotProps = {}, inputMode, decimalPlaces, allowNegative = false, phone = false,
    min, max, type: inputType, ...props }, ref) {
    const mode = inputMode || slotProps.input?.inputMode || (inputType === 'number' ? 'decimal' : undefined);
    const places = decimalPlaces ?? (mode === 'numeric' ? 0 : 6);
    const options = { decimalPlaces: places, allowNegative, phone };
    const [draft, setDraft] = useState(() => String(value ?? defaultValue ?? ''));
    const draftRef = useRef(draft);
    const focused = useRef(false);
    const write = text => { draftRef.current = text; setDraft(text); };
    useEffect(() => {
        if (value === undefined) return;
        const text = String(value ?? '');
        if (!focused.current || !sameNumericValue(draftRef.current, value)) {
            draftRef.current = text;
            setDraft(text);
        }
    }, [value]);
    const inputProps = owner => {
        const original = typeof slotProps.input === 'function' ? slotProps.input(owner) : (slotProps.input || {});
        const lower = min ?? original.min;
        const upper = max ?? original.max;
        return { ...original, type: phone ? 'tel' : 'text',
            inputMode: phone ? 'tel' : places === 0 ? 'numeric' : 'decimal',
            pattern: phone ? '\\+?[0-9 \\(\\)\\-]*' : `${allowNegative ? '-?' : ''}[0-9]+${places ? `(\\.[0-9]{0,${places}})?` : ''}`,
            autoComplete: phone ? (original.autoComplete || 'tel') : 'off',
            onChange: event => {
                const next = normalizeNumericText(event.target.value, options);
                if (next === null) { event.target.value = draftRef.current; return; }
                event.target.value = next;
                write(next);
                event.target.setCustomValidity(numericValidity(next, { min: lower, max: upper, phone }));
                original.onChange?.(event);
                onChange?.(event);
            },
            onFocus: event => { focused.current = true; original.onFocus?.(event); onFocus?.(event); },
            onBlur: event => {
                focused.current = false;
                original.onBlur?.(event); onBlur?.(event);
            },
            onKeyDown: event => {
                original.onKeyDown?.(event); onKeyDown?.(event);
                if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) return;
                const target = event.target;
                const start = target.selectionStart ?? draftRef.current.length;
                const end = target.selectionEnd ?? start;
                const candidate = draftRef.current.slice(0, start) + event.key + draftRef.current.slice(end);
                if (normalizeNumericText(candidate, options) === null) event.preventDefault();
            },
        };
    };
    return <Input {...props} ref={ref} type={phone ? 'tel' : 'text'} value={draft}
        slotProps={{ ...slotProps, input: inputProps }} />;
});

export default NumericInput;